import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  Typography,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import Navbar from '../components/Navbar';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import { User } from '../interface/User';
import { getUsersWithinMunicipality } from '../hooks/GetUserWithinMunicipality';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearMunicipality } from '../redux/municipialitySlice';
import { RootState } from '../store/store';
import { addMessage } from '../redux/infoMessageSlice';

const ViewMunicipality: React.FC = () => {
  const auth: string = useAuthHeader() || '';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');

  const municipality = useSelector(
    (state: RootState) => state.municipality.selectedMunicipality
  );

  const [currentUser, setCurrentuser] = useState<User | null>(null);
  const [usersWithinMunicipality, setUsersWithinMunicipality] = useState<
    User[]
  >([]);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);

  const { REACT_APP_API_URL } = process.env;
  const { t } = useTranslation();

  const fetchUsersWithinMunicipality = async () => {
    if (!municipality?.id) return;

    try {
      const result = await getUsersWithinMunicipality(auth, dispatch);
      if (Array.isArray(result)) {
        const username = localStorage.getItem('username');
        const user = result.find((u) => u.user.username === username);

        if (user) {
          setCurrentuser(user.user);
        }

        const mappedUsers = result.map((item) => ({
          id: item.user.id,
          email: item.user.email,
          username: item.user.username,
          role: item.role,
        }));
        setUsersWithinMunicipality(mappedUsers);
      } else {
        dispatch(
          addMessage({ message: 'error.failedToFetch', error: result.message })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    }
  };

  useEffect(() => {
    fetchUsersWithinMunicipality();
  }, [auth, municipality?.id]);

  const handleLeaveMunicipality = () => {
    if (!currentUser) return;
    setLeaveModalOpen(true);
  };

  const handleCloseModal = () => {
    setLeaveModalOpen(false);
  };

  const confirmLeaveMunicipality = async () => {
    if (!currentUser) return;

    try {
      const response = await fetch(
        `${REACT_APP_API_URL}/api/remove_user_from_municipality/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: auth,
          },
          body: JSON.stringify({ user_id: currentUser.id }),
        }
      );

      if (response.ok) {
        setCurrentuser(null);
        dispatch(clearMunicipality());
        navigate('/');
        dispatch(
          addMessage({
            message: 'municipality.leftMunicipality',
            type: 'success',
          })
        );
        setLeaveModalOpen(false);
      } else {
        dispatch(
          addMessage({
            message: 'error.failedToFetch',
            error: response.statusText,
          })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    }
  };

  if (!municipality) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Navbar />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom color="text.secondary">
            {t('municipality.notInMunicipality')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            {t('municipality.back')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Navbar />
      <Box
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            mt: isMobile ? '2vh' : '10vh',
            width: isMobile ? '95%' : '60%',
            height: isMobile ? 'auto' : '60%',
            boxShadow: isMobile ? 0 : 12,
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              flex: '0 1 auto',
              display: 'flex',
              ml: 2,
              mt: 2,
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.5rem', mr: 2 }}>
              {municipality.name}
            </Typography>
            {municipality.logo && (
              <img
                src={municipality.logo}
                alt="Municipality Logo"
                style={{
                  height: 40,
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              p: isMobile ? 1 : 4,
            }}
          >
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
              {t('municipality.userInMunicipality')}
            </Typography>
            <List
              id={'users-within-municipality-list'}
              sx={{ width: '100%', maxWidth: 800, p: 0, overflowY: 'auto' }}
            >
              {usersWithinMunicipality.length === 0 && (
                <Typography color="textSecondary">
                  {t('municipality.noUser')}
                </Typography>
              )}
              {usersWithinMunicipality.map((user) => (
                <ListItem
                  sx={{
                    p: 2,
                    mb: 1,
                    border: '1px solid #eee',
                    borderRadius: 1,
                    bgcolor:
                      user.username === currentUser?.username
                        ? '#f9f9f9'
                        : 'transparent',
                  }}
                  key={user.id}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight:
                            user.username === currentUser?.username
                              ? 'bold'
                              : 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {user.username}{' '}
                        {user.username === currentUser?.username}
                      </Typography>
                    }
                    secondary={`${user.email} | ${t(`municipality.${user.role}`)}`}
                    sx={{ wordBreak: 'break-word' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              p: 2,
              gap: 2,
              borderTop: isMobile ? 'none' : '1px solid #eee',
            }}
          >
            <Button
              id={'leave-municipality-button'}
              variant="contained"
              color="error"
              onClick={handleLeaveMunicipality}
              sx={{ mr: 1 }}
            >
              {t('municipality.leaveMunicipality')}
            </Button>
            <Button variant="contained" onClick={() => navigate('/')}>
              {t('municipality.back')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Modal open={leaveModalOpen} onClose={handleCloseModal}>
        <Box
          id={'confirmation-window'}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '85%' : 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {t('municipality.leaveMunicipality')}
          </Typography>
          <Typography
            sx={{ mb: 4 }}
            dangerouslySetInnerHTML={{
              __html: t('municipality.confirmationTextCurrentUser', {
                username: currentUser?.username,
              }),
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="contained"
              id={'abort-button'}
              onClick={handleCloseModal}
            >
              {t('municipality.abort')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmLeaveMunicipality}
            >
              {t('municipality.leave')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ViewMunicipality;
